
import {
  defineComponent,
  PropType,
  ref,
  computed,
  onUnmounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import {
  FieldConfig,
  ParserFieldObject,
} from '@/types';
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';

interface TestResult {
  isWin: boolean;
  message: string;
  prize?: any;
}

export default defineComponent({
  name: 'GameTestButton',

  components: {
    Button,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  setup(props) {
    const { t } = useI18n();
    const isTesting = ref(false);
    const testResult = ref<TestResult | null>(null);
    const hasPlayed = ref(false);

    const gameType = computed(() => props.configs.options?.gameType || 'wheel');

    const getGameInstance = () => {
      const iframeDoc = getTemplateIframeDocument();
      if (!iframeDoc) return null;

      const iframeWindow = iframeDoc.defaultView;
      if (!iframeWindow || !(iframeWindow as any).shopimind?.SPMGames) return null;

      const SPMGames = (iframeWindow as any).shopimind.SPMGames;

      if (gameType.value === 'wheel' && SPMGames.wheels.length > 0) {
        return SPMGames.wheels[SPMGames.wheels.length - 1];
      } else if (gameType.value === 'slotMachine' && SPMGames.slotMachines.length > 0) {
        return SPMGames.slotMachines[SPMGames.slotMachines.length - 1];
      } else if (gameType.value === 'scratchCard' && SPMGames.scratchCards.length > 0) {
        return SPMGames.scratchCards[SPMGames.scratchCards.length - 1];
      }
      return null;
    };

    const resetGame = () => {
      const gameInstance = getGameInstance();
      if (gameInstance && typeof gameInstance.reset === 'function') {
        gameInstance.reset();
      }
      testResult.value = null;
      hasPlayed.value = false;
    };

    const runTest = async () => {
      isTesting.value = true;
      testResult.value = null;

      try {
        const gameInstance = getGameInstance();

        if (!gameInstance) {
          console.error('GameTestButton: No game instance found for type', gameType.value);
          isTesting.value = false;
          return;
        }

        // Listen for the result event
        const resultPromise = new Promise<TestResult>((resolve) => {
          const handler = (event: CustomEvent) => {
            gameInstance.element.removeEventListener('spm:game:result', handler);
            resolve({
              isWin: event.detail.isWin,
              message: event.detail.isWin
                ? `${t('templateBuilder.fields.testWin')}: ${event.detail.prize?.label || ''}`
                : t('templateBuilder.fields.testLose'),
              prize: event.detail.prize,
            });
          };
          gameInstance.element.addEventListener('spm:game:result', handler);
        });

        // Trigger the game
        if (gameType.value === 'wheel') {
          gameInstance.spin();
        } else if (gameType.value === 'slotMachine') {
          gameInstance.spin();
        } else if (gameType.value === 'scratchCard') {
          // For scratch card, simulate scratching animation
          gameInstance.simulateScratch();
        }

        // Wait for result with timeout
        const timeoutPromise = new Promise<TestResult>((resolve) => {
          setTimeout(() => {
            resolve({
              isWin: false,
              message: t('templateBuilder.fields.testTimeout'),
            });
          }, 10000);
        });

        testResult.value = await Promise.race([resultPromise, timeoutPromise]);
        hasPlayed.value = true;
      } catch (error) {
        console.error('GameTestButton: Error running test', error);
        testResult.value = {
          isWin: false,
          message: t('templateBuilder.fields.testError'),
        };
      }

      isTesting.value = false;
    };

    // Auto-reset when component is unmounted (leaving the tab or closing panel)
    onUnmounted(() => {
      resetGame();
    });

    return {
      t,
      isTesting,
      testResult,
      hasPlayed,
      runTest,
      resetGame,
    };
  },
});
